import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";

export const FAQS = [
  {
    q: "What clinical services can we commission from NCS?",
    a: "You can commission Structured Medication Reviews, polypharmacy and deprescribing programmes, opioid reduction programmes, gabapentinoid prescribing reviews, high-risk medication reviews, valproate and medicines safety work, care home medication reviews, medicines reconciliation, prescribing audits, long-term condition medicines management, and bespoke PCN or ICB projects. Several services can be combined within one enquiry.",
  },
  {
    q: "How do we request clinical pharmacists or pharmacy technicians?",
    a: "Use the Request Clinical Workforce page to select the roles you need — clinical pharmacists, senior clinical pharmacists, independent prescribing pharmacists or pharmacy technicians — and tell us the hours, duration and working model. You can request multiple roles in a single enquiry, and we respond with a workforce proposal.",
  },
  {
    q: "Can we combine a managed clinical service and workforce in one enquiry?",
    a: "Yes. Many organisations commission a defined service (for example a Structured Medication Review programme) while also requesting pharmacist or technician capacity. Our enquiry form is designed for exactly that — one submission covers both.",
  },
  {
    q: "Is NCS support eligible for ARRS funding?",
    a: "Where your PCN is eligible, we can support ARRS-funded clinical pharmacy roles. However, NCS does not guarantee ARRS reimbursement, and not every service qualifies for ARRS funding. Eligibility always sits with NHS England scheme rules and your Integrated Care Board — we will help you structure support appropriately.",
  },
  {
    q: "Do your clinicians work on-site, remotely or in a hybrid model?",
    a: "All three. Many reviews, audits and reconciliation tasks can be delivered remotely, while care home rounds and patient-facing clinics are typically on-site. Most organisations choose a hybrid model — we will recommend what fits your service and patient cohort.",
  },
  {
    q: "How is clinical governance handled?",
    a: "Every engagement runs under defined clinical governance: named clinical oversight, verification of professional registration and competencies before delivery, clear escalation routes, and structured reporting on activity and outcomes throughout the contract.",
  },
  {
    q: "How quickly can support begin?",
    a: "Timescales depend on the scope, the roles required and notice periods, but we move quickly — submit an enquiry and we will respond with a proposed timeline. All start dates remain subject to availability, competency checks and a formal contract.",
  },
  {
    q: "Do we need to create an account to make an enquiry?",
    a: "No. The enquiry form takes around two minutes and requires no registration. Please do not include patient-identifiable or confidential clinical information — business contact details are processed under UK GDPR solely to respond to your enquiry.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-section" className="max-w-4xl mx-auto">
      <div className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q}>
              <button
                data-testid={`faq-question-${i + 1}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
              >
                <span className={`font-display text-base sm:text-lg font-bold transition-colors duration-300 ${
                  isOpen ? "text-[#10B981]" : "text-[#0B192C] group-hover:text-[#06B6D4]"
                }`}>
                  {f.q}
                </span>
                <span className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  isOpen ? "bg-[#10B981] border-[#10B981] text-white rotate-45" : "border-[#E2E8F0] text-[#0B192C]"
                }`}>
                  <Plus size={16} strokeWidth={2.5} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <p data-testid={`faq-answer-${i + 1}`} className="pb-7 pr-14 text-sm sm:text-base leading-relaxed text-[#475569]">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const FAQSection = () => (
  <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#06B6D4]">Common questions</p>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl mx-auto">
          Frequently Asked Questions for Healthcare Decision Makers
        </h2>
      </Reveal>
      <Reveal>
        <FAQ />
      </Reveal>
    </div>
  </section>
);

export default FAQ;
